import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { ownerAPI } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { useWebSocket } from '../hooks/useWebSocket';
import ModeToggle from '../components/ModeToggle';
import FamilyMembers from '../components/FamilyMembers';
import VisitorLogs from '../components/VisitorLogs';
import QRCodePanel from '../components/QRCodePanel';
import './DashboardPage.css';

const TABS = ['Overview', 'Family', 'Logs', 'QR Code'];

const DashboardPage = () => {
  const { user, logout, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('Overview');
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);

  // Load dashboard data
  const loadDashboard = useCallback(async () => {
    try {
      const res = await ownerAPI.dashboard();
      setDashboard(res.data.data);
    } catch {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  // Real-time WebSocket notifications
  const handleWSMessage = useCallback((event) => {
    const notification = {
      id: Date.now(),
      type: event.type,
      data: event.data,
      timestamp: event.timestamp
    };

    setNotifications(prev => [notification, ...prev.slice(0, 4)]);

    // Toast for visitor arrivals
    if (event.type === 'VISITOR_ARRIVAL') {
      toast('👋 Someone is at your door!', { icon: '🔔', duration: 5000 });
    } else if (event.type === 'VISITOR_MESSAGE') {
      toast(`✉️ New message: "${event.data.message?.substring(0, 40)}..."`, { duration: 5000 });
    } else if (event.type === 'PACKAGE_DELIVERY') {
      toast('📦 Package delivery logged at your door', { duration: 5000 });
    } else if (event.type === 'CALL_STATUS') {
      const label = event.data.status === 'ringing' ? '📞 Your phone is ringing!' : '📵 Call failed';
      toast(label, { duration: 4000 });
    }

    // Refresh logs when new activity
    if (activeTab === 'Logs') loadDashboard();
  }, [activeTab, loadDashboard]);

  useWebSocket(user?._id, handleWSMessage);

  const handleModeChange = async (newMode) => {
    try {
      await ownerAPI.setMode(newMode);
      updateUser({ mode: newMode });
      setDashboard(prev => prev ? { ...prev, user: { ...prev.user, mode: newMode } } : prev);
      toast.success(`Mode changed to ${newMode}`);
    } catch {
      toast.error('Failed to update mode');
    }
  };

  if (loading) {
    return (
      <div className="db-loading">
        <div className="spinner" style={{ width: 32, height: 32 }} />
        <p>Loading dashboard…</p>
      </div>
    );
  }

  const currentUser = dashboard?.user || user;
  const stats = dashboard?.stats || {};

  return (
    <div className="db-root">
      {/* Sidebar */}
      <aside className="db-sidebar">
        <div className="db-logo">
          <span>🔔</span>
          <span className="db-logo-text">QR Doorbell</span>
        </div>

        <nav className="db-nav">
          {TABS.map(tab => (
            <button
              key={tab}
              className={`db-nav-item ${activeTab === tab ? 'db-nav-item--active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              <span className="db-nav-icon">{tabIcon(tab)}</span>
              {tab}
            </button>
          ))}
        </nav>

        <div className="db-sidebar-footer">
          <div className="db-user-info">
            <div className="db-user-avatar">{currentUser?.name?.[0]?.toUpperCase()}</div>
            <div>
              <div className="db-user-name">{currentUser?.name}</div>
              <div className="db-user-email">{currentUser?.email}</div>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm db-logout-btn" onClick={logout}>
            Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="db-main">
        {/* Header */}
        <header className="db-header">
          <div>
            <h1 className="db-header-title">{activeTab}</h1>
            <p className="db-header-sub">
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          {/* Live notifications badge */}
          {notifications.length > 0 && (
            <div className="db-notification-badge">
              <span className="db-notification-dot" />
              {notifications[0].type === 'VISITOR_ARRIVAL' ? '👋 Visitor at door' :
               notifications[0].type === 'VISITOR_MESSAGE' ? '✉️ New message' :
               notifications[0].type === 'PACKAGE_DELIVERY' ? '📦 Package delivered' :
               '📞 Call activity'}
            </div>
          )}
        </header>

        {/* Tab content */}
        <div className="db-content">
          {activeTab === 'Overview' && (
            <OverviewTab
              user={currentUser}
              stats={stats}
              recentLogs={dashboard?.recentLogs || []}
              qrCode={dashboard?.qrCode}
              doorUrl={dashboard?.doorUrl}
              onModeChange={handleModeChange}
              onTabChange={setActiveTab}
            />
          )}
          {activeTab === 'Family' && (
            <FamilyMembers
              members={currentUser?.familyMembers || []}
              onUpdate={loadDashboard}
            />
          )}
          {activeTab === 'Logs' && <VisitorLogs />}
          {activeTab === 'QR Code' && (
            <QRCodePanel
              qrCode={dashboard?.qrCode}
              doorUrl={dashboard?.doorUrl}
              doorId={currentUser?.doorId}
            />
          )}
        </div>
      </main>
    </div>
  );
};

// ─── Overview Tab ──────────────────────────────────────────────────────────────
const OverviewTab = ({ user, stats, recentLogs, qrCode, doorUrl, onModeChange, onTabChange }) => (
  <div className="db-overview animate-fade-in">
    {/* Mode toggle - most important control */}
    <section className="db-section">
      <h3 className="db-section-title">Current Status</h3>
      <ModeToggle currentMode={user?.mode} onModeChange={onModeChange} />
    </section>

    {/* Stats cards */}
    <section className="db-section">
      <h3 className="db-section-title">Activity</h3>
      <div className="db-stats-grid">
        <StatCard label="Today" value={stats.today ?? '—'} icon="📅" />
        <StatCard label="This Week" value={stats.week ?? '—'} icon="📆" />
        <StatCard label="All Time" value={stats.total ?? '—'} icon="📊" />
      </div>
    </section>

    {/* Recent activity */}
    <section className="db-section">
      <div className="db-section-header">
        <h3 className="db-section-title">Recent Activity</h3>
        <button className="btn btn-ghost btn-sm" onClick={() => onTabChange('Logs')}>
          View all →
        </button>
      </div>
      {recentLogs.length === 0 ? (
        <div className="db-empty">
          <span>🚪</span>
          <p>No visitors yet. Share your QR code to get started.</p>
        </div>
      ) : (
        <div className="db-recent-logs">
          {recentLogs.slice(0, 5).map(log => (
            <LogRow key={log._id} log={log} />
          ))}
        </div>
      )}
    </section>

    {/* Quick QR preview */}
    {qrCode && (
      <section className="db-section">
        <div className="db-section-header">
          <h3 className="db-section-title">Your Door QR</h3>
          <button className="btn btn-ghost btn-sm" onClick={() => onTabChange('QR Code')}>
            Manage →
          </button>
        </div>
        <div className="db-qr-preview">
          <img src={qrCode} alt="QR Code" className="db-qr-thumb" />
          <div>
            <p className="text-secondary text-sm">Place this at your door</p>
            <p className="text-xs text-muted mt-sm">{doorUrl}</p>
          </div>
        </div>
      </section>
    )}
  </div>
);

const StatCard = ({ label, value, icon }) => (
  <div className="db-stat-card">
    <span className="db-stat-icon">{icon}</span>
    <span className="db-stat-value">{value}</span>
    <span className="db-stat-label">{label}</span>
  </div>
);

const LogRow = ({ log }) => {
  const icons = {
    SCAN: '👁️', CALL_REQUEST: '📲', MESSAGE: '✉️',
    PACKAGE: '📦', CALL_CONNECTED: '📞', CALL_FAILED: '📵', CALL_DECLINED: '🚫'
  };
  const labels = {
    SCAN: 'Door scanned', CALL_REQUEST: 'Call requested', MESSAGE: 'Left a message',
    PACKAGE: 'Package delivered', CALL_CONNECTED: 'Call connected',
    CALL_FAILED: 'Call failed', CALL_DECLINED: 'Call declined'
  };

  return (
    <div className="db-log-row">
      <span className="db-log-icon">{icons[log.action] || '📌'}</span>
      <div className="db-log-info">
        <span className="db-log-action">{labels[log.action] || log.action}</span>
        {log.content && (
          <span className="db-log-content">"{log.content.substring(0, 60)}{log.content.length > 60 ? '…' : ''}"</span>
        )}
      </div>
      <span className="db-log-time">
        {new Date(log.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
      </span>
    </div>
  );
};

const tabIcon = (tab) => {
  const icons = { Overview: '⊡', Family: '👥', Logs: '📋', 'QR Code': '⬛' };
  return icons[tab] || '·';
};

export default DashboardPage;
